/**
 * Homeowner account export and deletion.
 * The phone only sends a session token. The function deletes the auth user
 * and the homeowner's rows. Invite-code and password-setup tokens cannot delete.
 */

const DELETE_PHRASE = 'DELETE';

const SECRET_KEYS = [
  'password_hash',
  'password_reset_token',
  'password_reset_expires',
  'invite_code',
];

/** Personal rows keyed by the homeowner. Order is children, then the client. */
const OWNED_ROWS = [
  { table: 'task_checkins', column: 'client_id' },
  { table: 'reminder_log', column: 'client_id' },
  { table: 'client_answers', column: 'client_id' },
  { table: 'client_task_series', column: 'client_id' },
  { table: 'client_tasks', column: 'client_id' },
  { table: 'client_documents', column: 'client_id' },
  { table: 'client_systems', column: 'client_id' },
  { table: 'property_contacts', column: 'client_id' },
  { table: 'task_help_requests', column: 'client_id' },
  { table: 'ai_conversations', column: 'client_id' },
  { table: 'notification_preferences', column: 'client_id' },
  { table: 'notification_queue', column: 'client_id' },
  { table: 'client_email_preferences', column: 'client_id' },
  { table: 'projects', column: 'client_id' },
  { table: 'vendor_clicks', column: 'client_id' },
  { table: 'device_tokens', column: 'user_id' },
  { table: 'client_invites', column: 'client_id' },
  { table: 'homeowner_intake_events', column: 'client_id' },
  { table: 'consent_records', column: 'user_id' },
];

const STORAGE_BUCKETS = ['client-documents', 'property-photos'];

function accountActionDecision({ scope, action, confirm }) {
  if (scope !== 'session') {
    return {
      status: 401,
      error: 'Sign in with your email and password to manage this account.',
    };
  }
  if (action === 'export') return { ok: 'export' };
  if (action === 'logout-all') return { ok: 'logout-all' };
  if (action === 'delete') {
    if (confirm !== DELETE_PHRASE) {
      return { status: 400, error: 'Type DELETE to delete this account.' };
    }
    return { ok: 'delete' };
  }
  return { status: 400, error: 'Unsupported account action' };
}

function stripSecrets(row) {
  if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
  const copy = { ...row };
  SECRET_KEYS.forEach((key) => {
    delete copy[key];
  });
  return copy;
}

function exportPayload(client, bundles, now = new Date()) {
  const data = {
    exported_at: now.toISOString(),
    account: stripSecrets(client),
  };
  const source = bundles && typeof bundles === 'object' ? bundles : {};
  Object.keys(source).forEach((table) => {
    const rows = Array.isArray(source[table]) ? source[table] : [];
    data[table] = rows.map(stripSecrets);
  });
  return data;
}

function exportFilename(now = new Date()) {
  const day = now.toISOString().slice(0, 10);
  return `pacarion-my-data-${day}.json`;
}

function isMissingRelation(error) {
  if (!error) return false;
  const code = String(error.code || '');
  const message = String(error.message || '');
  return code === '42P01'
    || code === 'PGRST205'
    || /does not exist|schema cache|could not find the table/i.test(message);
}

function authUserMissing(error) {
  if (!error) return false;
  const message = String(error.message || error.error_description || '');
  return /not found|user not found/i.test(message);
}

function documentPaths(clientId, documents) {
  const prefix = `${clientId}/`;
  return (documents || [])
    .map((doc) => doc && doc.file_path)
    .filter((path) => typeof path === 'string' && path.startsWith(prefix));
}

/**
 * Deletes the homeowner's files, rows, client record, then the auth user.
 * The auth user is last because clients.user_id references auth.users.
 */
async function deleteHomeownerAccount(store, client) {
  if (!store || !client || !client.id) {
    throw new Error('Homeowner account is missing');
  }
  const clientId = client.id;
  const documents = await store.list('client_documents', 'client_id', clientId);
  for (const bucket of STORAGE_BUCKETS) {
    const extra = bucket === 'client-documents' ? documentPaths(clientId, documents) : [];
    await store.removeStorage(bucket, clientId, extra);
  }
  for (const spec of OWNED_ROWS) {
    await store.deleteWhere(spec.table, spec.column, clientId);
  }
  if (client.user_id && client.user_id !== clientId) {
    await store.deleteWhere('consent_records', 'user_id', client.user_id);
    await store.deleteWhere('device_tokens', 'user_id', client.user_id);
  }
  const email = String(client.email || '').trim().toLowerCase();
  if (email) {
    await store.deleteWhere('email_suppressions', 'email_normalized', email);
  }
  await store.deleteWhere('clients', 'id', clientId);
  if (client.user_id) {
    await store.deleteAuthUser(client.user_id);
  }
}

async function collectHomeownerExport(store, clientId) {
  const bundles = {};
  for (const spec of OWNED_ROWS) {
    bundles[spec.table] = await store.list(spec.table, spec.column, clientId);
  }
  return bundles;
}

function createSupabaseAccountStore(supabase) {
  return {
    async list(table, column, value) {
      const { data, error } = await supabase.from(table).select('*').eq(column, value);
      if (isMissingRelation(error)) return [];
      if (error) throw error;
      return data || [];
    },
    async deleteWhere(table, column, value) {
      const { error } = await supabase.from(table).delete().eq(column, value);
      if (error && !isMissingRelation(error)) throw error;
    },
    async removeStorage(bucket, clientId, extraPaths) {
      const listed = await supabase.storage.from(bucket).list(String(clientId));
      if (listed.error && !isMissingRelation(listed.error)) throw listed.error;
      const names = (listed.data || []).map((item) => item && item.name).filter(Boolean);
      const paths = names.map((name) => `${clientId}/${name}`);
      (extraPaths || []).forEach((path) => {
        if (path && paths.indexOf(path) === -1) paths.push(path);
      });
      if (paths.length === 0) return;
      const removed = await supabase.storage.from(bucket).remove(paths);
      if (removed.error && !isMissingRelation(removed.error)) throw removed.error;
    },
    async deleteAuthUser(userId) {
      const { error } = await supabase.auth.admin.deleteUser(userId);
      if (error && !authUserMissing(error)) throw error;
    },
  };
}

export {
  DELETE_PHRASE,
  OWNED_ROWS,
  STORAGE_BUCKETS,
  accountActionDecision,
  stripSecrets,
  exportPayload,
  exportFilename,
  isMissingRelation,
  documentPaths,
  deleteHomeownerAccount,
  collectHomeownerExport,
  createSupabaseAccountStore,
};
